import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";
import AuditLogPanel from "../common/AuditLogPanel";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast } from "../../shared/Toast";
import { listBanners, getBannerApps, getBanner, toggleBanner, duplicateBanner } from "../../utils/appBannerApi";
import {
    STATUS_META,
    BANNER_TYPE_LABELS,
    BANNER_AUDIT_ACTION_LABELS,
    BANNER_AUDIT_FIELD_LABELS,
    bannerAuditResolve,
    deriveStatus,
    describeWindow,
    describeFrequency,
} from "../../constants/appBanners";
import BannerComposerModal from "./BannerComposerModal";

/**
 * Marketing → App Banners.
 *
 * Merchant-scoped like every other page: a merchant user is pinned to its own merchant, a
 * platform admin uses the header switcher.
 *
 * A banner's state is DERIVED from its window rather than stored, so the badges here move on
 * their own as a schedule starts and expires — nothing has to write a column on a timer.
 *
 * The advisory above the table is the answer to the question this screen invites: several
 * live banners do not queue up as separate popups, they become one swipeable card stack in
 * the order shown here.
 */

const PAGE_LIMIT = 25;
const IMAGE_BASE = process.env.REACT_APP_URL || "";

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white, color: C.text, outline: "none",
};
const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = { ...buttonStyle, background: C.white, color: C.textMed, border: `1px solid ${C.border}`, boxShadow: "none" };
const linkBtn = { padding: "4px 8px", border: "none", background: "none", color: C.coral, fontSize: 12, fontWeight: 600, cursor: "pointer" };

const StatusBadge = ({ banner }) => {
    const m = STATUS_META[deriveStatus(banner)];
    return <Badge color={m.color} bg={m.bg}>{m.label}</Badge>;
};

/** The one-line summary of what a customer reads, for the table. */
const summarise = (b) => {
    if (b.bannerType === "rich") return (b.htmlEn || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return b.titleEn || b.bodyEn || "";
};

const AppBanners = () => {
    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const merchantId = isMerchantScoped(userType) ? loggedInMerchantId : reduxSelectedMerchant;
    const readOnly = isReadOnly(userType);
    const noMerchant = !merchantId || merchantId === "all";

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(PAGE_LIMIT);
    const [status, setStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [apps, setApps] = useState({ linked: true, apps: [] });

    const [composerOpen, setComposerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null); // { banner, next }
    const [busy, setBusy] = useState(false);
    const [historyOf, setHistoryOf] = useState(null);
    const [auditKey, setAuditKey] = useState(0);

    const load = useCallback(async () => {
        if (noMerchant) { setItems([]); setTotal(0); return; }
        setLoading(true);
        const data = await listBanners({
            merchantId,
            status: status === "all" ? undefined : status,
            search: search || undefined,
            page,
            limit,
        });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
    }, [merchantId, noMerchant, status, search, page, limit]);

    const loadApps = useCallback(async () => {
        if (noMerchant) return;
        setApps(await getBannerApps(merchantId));
    }, [merchantId, noMerchant]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadApps(); }, [loadApps]);

    const refresh = async () => { await load(); setAuditKey((k) => k + 1); };

    const openNew = () => { setEditing(null); setComposerOpen(true); };
    const openEdit = async (b) => { setEditing(await getBanner(b.id) || b); setComposerOpen(true); };

    const runToggle = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            const r = await toggleBanner(confirm.banner.id, confirm.next);
            if (r) successToast(confirm.next ? "Banner switched on" : "Banner switched off");
            setConfirm(null);
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const handleDuplicate = async (b) => {
        const r = await duplicateBanner(b.id);
        if (r) { successToast("Copy created — it starts switched off"); await refresh(); openEdit(r); }
    };

    const applySearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };

    const liveNow = useMemo(() => items.filter((b) => deriveStatus(b) === "live"), [items]);
    const impressions = items.reduce((s, b) => s + (b.impressions || 0), 0);
    const clicks = items.reduce((s, b) => s + (b.clicks || 0), 0);

    return (
        <Fragment>
            <Breadcrumb title="App Banners" parent="Marketing" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    {noMerchant ? (
                        <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Select a merchant to manage its app banners.</div></RCard>
                    ) : (
                        <>
                            {!apps.linked && (
                                <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 12, background: C.coralLight, color: C.coral, fontWeight: 600, fontSize: 13 }}>
                                    {apps.message || "No app is linked to this merchant."} Banners will not reach anybody until a platform admin links an active app under <b>Apps</b>.
                                </div>
                            )}

                            <div className="rpt-row" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
                                <Tile label="Banners" value={total} sub="for this merchant" />
                                <Tile label="Live right now" value={liveNow.length} sub="switched on and inside their window" />
                                <Tile label="Times shown" value={impressions.toLocaleString()} sub="banners on this page" />
                                <Tile label="Tapped" value={impressions ? `${Math.round((clicks / impressions) * 100)}%` : "—"} sub={`${clicks.toLocaleString()} taps on this page`} />
                            </div>

                            {liveNow.length > 1 && (
                                <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 12, background: C.blueLight, color: C.blue, fontWeight: 600, fontSize: 13 }}>
                                    {liveNow.length} banners are live. Customers do not get {liveNow.length} popups — they see one card they can swipe through, in the display order below.
                                </div>
                            )}

                            <RCard>
                                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
                                    <form onSubmit={applySearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 260 }}>
                                        <input style={{ ...inputStyle, flex: 1 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name or title…" />
                                        <button type="submit" style={btnSecondary}>Search</button>
                                    </form>
                                    <select style={inputStyle} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                                        <option value="all">On and off</option>
                                        <option value="active">Switched on</option>
                                        <option value="inactive">Switched off</option>
                                    </select>
                                    {!readOnly && <button onClick={openNew} style={buttonStyle}>＋ New banner</button>}
                                </div>

                                {loading && items.length === 0 ? <Loader /> : items.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                        {search || status !== "all" ? "No banners match these filters." : "No banners yet. Create one to greet customers when they open the app."}
                                    </div>
                                ) : (
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...tbl.th, width: 60 }}>Order</th>
                                                    <th style={tbl.th}>Banner</th>
                                                    <th style={tbl.th}>Type</th>
                                                    <th style={tbl.th}>When</th>
                                                    <th style={tbl.th}>How often</th>
                                                    <th style={tbl.th}>Status</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Shown</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Tapped</th>
                                                    <th style={tbl.th}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((b) => (
                                                    <tr key={b.id}>
                                                        <td style={{ ...tbl.td, color: C.textMed, fontWeight: 700 }}>{b.sortOrder}</td>
                                                        <td style={tbl.td}>
                                                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                                                {b.imagePath && (
                                                                    <img
                                                                        src={`${IMAGE_BASE}${b.imagePath}`}
                                                                        alt=""
                                                                        style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: `1px solid ${C.borderLight}` }}
                                                                    />
                                                                )}
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div style={{ fontWeight: 700, color: C.text }}>{b.name}</div>
                                                                    <div style={{ fontSize: 12, color: C.textMed, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={summarise(b)}>
                                                                        {summarise(b) || <em style={{ color: C.textLight }}>image only</em>}
                                                                    </div>
                                                                    {b.app ? <div style={{ fontSize: 11, color: C.textLight }}>{b.app.name || b.app.reference}</div> : null}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>
                                                            {BANNER_TYPE_LABELS[b.bannerType] || b.bannerType}
                                                            {!b.dismissible && <div style={{ fontSize: 11, color: C.orange, fontWeight: 600 }}>cannot be closed</div>}
                                                        </td>
                                                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed, whiteSpace: "nowrap" }}>{describeWindow(b)}</td>
                                                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed, whiteSpace: "nowrap" }}>{describeFrequency(b)}</td>
                                                        <td style={tbl.td}><StatusBadge banner={b} /></td>
                                                        <td style={{ ...tbl.td, textAlign: "right", fontSize: 12 }}>{(b.impressions || 0).toLocaleString()}</td>
                                                        <td style={{ ...tbl.td, textAlign: "right", fontSize: 12 }}>
                                                            {b.impressions ? `${b.clicks} (${Math.round((b.clicks / b.impressions) * 100)}%)` : "—"}
                                                        </td>
                                                        <td style={{ ...tbl.td, whiteSpace: "nowrap", textAlign: "right" }}>
                                                            {!readOnly && <button style={linkBtn} onClick={() => openEdit(b)}>Edit</button>}
                                                            {!readOnly && <button style={linkBtn} onClick={() => handleDuplicate(b)}>Duplicate</button>}
                                                            {!readOnly && (
                                                                <button
                                                                    style={{ ...linkBtn, color: b.isActive ? C.coral : C.green }}
                                                                    onClick={() => setConfirm({ banner: b, next: !b.isActive })}
                                                                >
                                                                    {b.isActive ? "Switch off" : "Switch on"}
                                                                </button>
                                                            )}
                                                            <button style={linkBtn} onClick={() => setHistoryOf(b)}>History</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <TablePagination total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
                                    </div>
                                )}
                            </RCard>
                        </>
                    )}
                </div>
            </Container>

            <BannerComposerModal
                isOpen={composerOpen}
                onClose={() => setComposerOpen(false)}
                merchantId={merchantId}
                apps={apps.apps}
                banner={editing}
                imageBase={IMAGE_BASE}
                onSaved={refresh}
            />

            <Modal isOpen={Boolean(confirm)} toggle={() => !busy && setConfirm(null)}>
                <ModalHeader toggle={() => !busy && setConfirm(null)}>
                    {confirm?.next ? "Switch this banner on?" : "Switch this banner off?"}
                </ModalHeader>
                <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
                    {confirm && (confirm.next ? (
                        <>“<b style={{ color: C.text }}>{confirm.banner.name}</b>” starts appearing to customers who open the app, as soon as its window allows.</>
                    ) : (
                        <>“<b style={{ color: C.text }}>{confirm.banner.name}</b>” stops appearing immediately. Nothing is deleted — its history and counters are kept, and you can switch it back on at any time.</>
                    ))}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setConfirm(null)} disabled={busy} style={btnSecondary}>Back</button>
                    <button onClick={runToggle} disabled={busy} style={buttonStyle}>
                        {busy ? "Working…" : confirm?.next ? "Yes, switch it on" : "Yes, switch it off"}
                    </button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={Boolean(historyOf)} toggle={() => setHistoryOf(null)} size="lg">
                <ModalHeader toggle={() => setHistoryOf(null)}>History — {historyOf?.name}</ModalHeader>
                <ModalBody>
                    {historyOf && (
                        <AuditLogPanel
                            key={`${historyOf.id}-${auditKey}`}
                            merchantId={merchantId}
                            module="marketing"
                            entityType="app_banner"
                            entityId={historyOf.id}
                            actionLabels={BANNER_AUDIT_ACTION_LABELS}
                            fieldLabels={BANNER_AUDIT_FIELD_LABELS}
                            resolve={bannerAuditResolve}
                            title="Changes to this banner"
                        />
                    )}
                </ModalBody>
            </Modal>
        </Fragment>
    );
};

export default AppBanners;
